import React, {forwardRef, Fragment, useImperativeHandle, useState} from "react";
import {useTranslation} from "react-i18next";
import {
  Button,
  Dialog,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {Delete} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import DepartmentService from "@/services/hr/DepartmentService";
import {useSnackbar} from "notistack";
import {CancelButton, SavingButton} from "@/components/data-grid/CustomButton";
import SnackbarUtil from "@/utils/SnackbarUtil";
import GenericService from "@/services/GenericService";
import {ATTEndpoints} from "@/services/att/endpoints";
import ActionButton from "@/components/actions/ActionButton";

const dialogStyle = {
  "& .MuiPaper-root": {width: "400px", top: "1vh", overflowX: "hidden"},
  ".MuiDialog-paper": {maxWidth: "100vw"},
  overflowX: "hidden",
};

export function DeleteAction({setInfo}: any) {
  const {t} = useTranslation();

  return (
    <Tooltip title={t("common.Delete Timesheet?")}>
      <ActionButton
        title={t("common.delete")}
        icon="icon-ic_delete"
        handleClick={() => {
          setInfo(true);
        }}
      ></ActionButton>
    </Tooltip>
  );
}


const TimesheetDelete = (props: any, ref: any) => {
  const [info, setInfo] = useState<boolean>(false);
  const [timesheetIds, setTimesheetIds] = useState<any>([])
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar();
  const [isLoading, setLoading] = useState<boolean>(false);
  useImperativeHandle(ref, () => ({
    showDelete: async (id: string) => {
      setTimesheetIds(id)
      setInfo(false)
    }
  }));
  const toggleDrawer =
    (open: boolean) =>
      (event: React.KeyboardEvent | React.MouseEvent) => {
        if (timesheetIds.length == 0) {
          setInfo(false);
          SnackbarUtil.error(t("common.Select Timesheet first"),
            {anchorOrigin: {vertical: 'top', horizontal: 'center'}})
          return;
        }
      };
  const onSubmit = async (event: any) => {
    try {
      setLoading(true)
      // 避免空数组导致的错误
      if (!timesheetIds || timesheetIds.length === 0) {
        enqueueSnackbar(t("common.no data to delete"), {
          variant: "warning",
          anchorOrigin: {vertical: "top", horizontal: "center"}
        });
        return;
      }

      // 收集所有删除操作的Promise
      const deletePromises = timesheetIds.map(async (timesheet: any) => {
        try {
          // 等待单个删除操作完成
          await GenericService.destroy(ATTEndpoints.ATT_TIMESHEET_URI, `${timesheet}`);
          // 单个删除成功后提示（如果需要每条成功都提示）
          enqueueSnackbar(t("common.success"), {
            variant: "success",
            anchorOrigin: {vertical: "top", horizontal: "center"}
          });
          return true; // 标记当前删除成功
        } catch (error) {
          console.error(`删除timesheet ${timesheet} 失败:`, error);
          return false; // 标记当前删除失败
        }
      });

      // 等待所有删除操作完成
      await Promise.all(deletePromises);

      // 所有操作完成后刷新表格（无论成功失败，确保数据同步）
      if (props.reloadTable) {
        props.reloadTable();
      }

    } catch (e) {
      console.log("error===", e);
    } finally {
      setInfo(false); // 无论成功失败，都更新状态
      setLoading(false)
    }
  };

  return (
    <Fragment>
      <Grid item={true} onClick={toggleDrawer(true)}>
        <DeleteAction setInfo={setInfo}/>
      </Grid>
      <Dialog
        open={info}
        // onClose={()=>setInfo(false)}
        sx={dialogStyle}
      >
        <DialogTitle style={{marginBottom: "-25px"}}>
          {t("common.Delete Timesheet?")}
          <IconButton
            aria-label="close"
            onClick={() => setInfo(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon/>
          </IconButton>
        </DialogTitle>
        <Typography sx={{p: 3}}>
          {t("common.Are you sure to delete these Timesheet?")}
        </Typography>
        <Grid style={{marginBottom: "20px", marginRight: "20px", marginLeft: "20px"}}>
          <Stack spacing={3} direction={"row"} justifyContent="flex-end">
            <CancelButton
              onClick={() => {
                setInfo(false);
              }}
            />
            <SavingButton loading={isLoading} onClick={onSubmit} children={t("common.confirm")}/>
          </Stack>
        </Grid>
      </Dialog>
    </Fragment>
  );
};

export default forwardRef(TimesheetDelete);
